import React from 'react';
import { TextField } from '@mui/material';
import Card from '../general/Card';
import { CCReport } from './construction-cost-report.type';
import { useFormik } from 'formik';
import WhiteFab from '../common/WhiteFab';
import { Close, Edit } from '@mui/icons-material';
import { Button } from 'components/index';

interface CCReportInputsCardProps {
  report: CCReport;
  updateReport: (updatedReport: CCReport) => void;
}

const CCReportProjectInfo: React.FC<CCReportInputsCardProps> = ({ report, updateReport }) => {
  const [isEditMode, setIsEditMode] = React.useState(false);
  const toggleEditMode = () => setIsEditMode(val => !val);

  React.useEffect(() => {
    setIsEditMode(false);
  }, [report]);

  const initialOrDefaultValues: Partial<CCReport> = {
    clientName: report.clientName,
    clientCompany: report.clientCompany,
    clientAddress: report.clientAddress,
    cdccRepresentatives: report.cdccRepresentatives,
    projectNumber: report.projectNumber,
  };

  const formik = useFormik({
    initialValues: initialOrDefaultValues,
    enableReinitialize: true,
    onSubmit: values => {
      const trimmedValues = {
        clientName: values.clientName?.trim(),
        clientCompany: values.clientCompany?.trim(),
        clientAddress: values.clientAddress?.trim(),
        projectNumber: values.projectNumber?.trim(),
        cdccRepresentatives: values.cdccRepresentatives?.trim(),
      };

      updateReport({
        ...report,
        ...trimmedValues,
      });
      setIsEditMode(false);
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    setIsEditMode(false);
  };

  const canSubmit = React.useMemo(() => {
    if (!formik.isValid) return false;
    return formik.dirty;
  }, [formik]);

  return (
    <form onSubmit={formik.handleSubmit} style={{ height: '100%' }}>
      <Card
        heading='Project Info'
        itemsPerRow={3}
        headerAction={
          isEditMode ? (
            <WhiteFab aria-label='cancel' icon={<Close fontSize='small' />} textColor='error' size='small' onClick={handleCancel} />
          ) : (
            <WhiteFab aria-label='edit' icon={<Edit fontSize='small' />} textColor='primary' size='small' onClick={toggleEditMode} />
          )
        }
        footer={
          isEditMode ? (
            <Button disabled={!canSubmit} onClick={formik.submitForm} sx={{ marginLeft: 'auto' }}>
              Save Draft
            </Button>
          ) : undefined
        }
        data={
          isEditMode
            ? {
                'Client Name': (
                  <TextField
                    name='clientName'
                    variant='standard'
                    value={formik.values.clientName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.clientName && Boolean(formik.errors.clientName)}
                    helperText={formik.touched.clientName && formik.errors.clientName}
                  />
                ),
                'Client Company': (
                  <TextField
                    name='clientCompany'
                    variant='standard'
                    value={formik.values.clientCompany}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.clientCompany && Boolean(formik.errors.clientCompany)}
                    helperText={formik.touched.clientCompany && formik.errors.clientCompany}
                  />
                ),
                'Client Address': (
                  <TextField
                    name='clientAddress'
                    variant='standard'
                    value={formik.values.clientAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.clientAddress && Boolean(formik.errors.clientAddress)}
                    helperText={formik.touched.clientAddress && formik.errors.clientAddress}
                  />
                ),
                'Project Number': (
                  <TextField
                    name='projectNumber'
                    variant='standard'
                    value={formik.values.projectNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.projectNumber && Boolean(formik.errors.projectNumber)}
                    helperText={formik.touched.projectNumber && formik.errors.projectNumber}
                  />
                ),
                'CDCC Representatives': (
                  <TextField
                    name='cdccRepresentatives'
                    variant='standard'
                    value={formik.values.cdccRepresentatives}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.cdccRepresentatives && Boolean(formik.errors.cdccRepresentatives)}
                    helperText={formik.touched.cdccRepresentatives && formik.errors.cdccRepresentatives}
                  />
                ),
              }
            : {
                'Client Name': report.clientName || '--',
                'Client Company': report.clientCompany || '--',
                'Client Address': report.clientAddress || '--',
                'Project Number': report.projectNumber || '--',
                'CDCC Representatives': report.cdccRepresentatives || '--',
              }
        }
      />
    </form>
  );
};

export default CCReportProjectInfo;
